import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi, resolverImagen } from '../hooks/useApi';
import Lightbox from '../components/Lightbox';
import './Pokedex.css';

const TYPE_COLORS = {
  'Planta':   { bg: '#14532d', text: '#86efac' },
  'Fuego':    { bg: '#7c2d12', text: '#fdba74' },
  'Agua':     { bg: '#1e3a5f', text: '#93c5fd' },
  'Eléctrico':{ bg: '#713f12', text: '#fde047' },
  'Cristal':  { bg: '#164e63', text: '#a5e3ff' },
  'Roca':     { bg: '#3d2b1f', text: '#c4a882' },
  'Hielo':    { bg: '#1e3a5f', text: '#bae6fd' },
  'Psíquico': { bg: '#4c1d95', text: '#d8b4fe' },
  'Fantasma': { bg: '#1e1b4b', text: '#a5b4fc' },
  'Siniestro':{ bg: '#1c1917', text: '#a8a29e' },
  'Veneno':   { bg: '#3b0764', text: '#d8b4fe' },
  'Lucha':    { bg: '#7c2d12', text: '#fca5a5' },
  'Bicho':    { bg: '#1a2e05', text: '#bef264' },
  'Volador':  { bg: '#1e3a5f', text: '#bae6fd' },
  'Tierra':   { bg: '#431407', text: '#fcd34d' },
  'Dragón':   { bg: '#1e1b4b', text: '#a5b4fc' },
  'Acero':    { bg: '#1c1917', text: '#d4d4d8' },
  'Normal':   { bg: '#292524', text: '#d6d3d1' },
  'Hada':     { bg: '#500724', text: '#fbcfe8' },
};

function TypeBadge({ tipo }) {
  const c = TYPE_COLORS[tipo] || { bg: '#292524', text: '#d6d3d1' };
  return <span className="type-badge" style={{ background: c.bg, color: c.text }}>{tipo}</span>;
}

function StatBar({ label, value }) {
  const pct = Math.min((value / 255) * 100, 100);
  return (
    <div className="stat-bar">
      <span className="stat-bar-label">{label}</span>
      <div className="stat-bar-track">
        <motion.div
          className="stat-bar-fill"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
      <span className="stat-bar-value">{value}</span>
    </div>
  );
}

function PokemonCard({ p, onClick }) {
  return (
    <motion.button
      className="poke-card"
      onClick={() => onClick(p)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      layout
    >
      <div className="poke-card-header">
        <span className="poke-id">{p.id}</span>
        <div className="poke-types">
          {p.tipos.map(t => <TypeBadge key={t} tipo={t} />)}
        </div>
      </div>
      <div className="poke-sprite-area">
        {p.imagen
          ? <img src={resolverImagen(p.imagen)} alt={p.nombre} className="poke-sprite" />
          : <div className="poke-sprite-placeholder">?</div>
        }
      </div>
      <div className="poke-card-footer">
        <h3 className="poke-name">{p.nombre}</h3>
        <p className="poke-desc-preview">
          {p.descripcion ? p.descripcion.slice(0, 60) + '...' : 'Sin descripción aún.'}
        </p>
      </div>
    </motion.button>
  );
}

function PokemonDetail({ p, onClose }) {
  const [lightbox, setLightbox] = useState(null);
  const total = p.stats
    ? Object.values(p.stats).reduce((a, b) => a + (Number(b) || 0), 0)
    : 0;
  const imgSrc = resolverImagen(p.imagen);

  return (
    <>
      <motion.div
        className="poke-detail-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="poke-detail parchment-card"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          onClick={e => e.stopPropagation()}
        >
          <button className="poke-detail-close" onClick={onClose}>✕</button>

          <div className="notebook-spiral">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="spiral-ring" />)}
          </div>

          <div className="poke-detail-inner">
            <div className="poke-detail-left">
              <div className="poke-detail-sprite-area">
                {imgSrc
                  ? <img
                      src={imgSrc}
                      alt={p.nombre}
                      className="poke-detail-sprite img-zoomable"
                      onClick={() => setLightbox(imgSrc)}
                    />
                  : <div className="poke-sprite-big-placeholder">?</div>
                }
              </div>
              <div className="poke-detail-types">
                {p.tipos.map(t => <TypeBadge key={t} tipo={t} />)}
              </div>
            </div>

            <div className="poke-detail-right">
              <div className="poke-detail-header">
                <span className="poke-detail-id">{p.id}</span>
                <h2 className="poke-detail-name">{p.nombre}</h2>
              </div>
              <div className="notebook-line-group">
                <p className="poke-detail-desc">{p.descripcion || 'Sin descripción aún.'}</p>
              </div>
              <div className="notebook-line-group">
                <h4 className="notebook-section-title">◈ Habilidades</h4>
                <div className="poke-abilities">
                  {(p.habilidades || []).map(h => <span key={h} className="ability-tag">{h}</span>)}
                </div>
              </div>
              <div className="notebook-line-group">
                <h4 className="notebook-section-title">◈ Stats Base <span className="total-badge">Total: {total}</span></h4>
                {p.stats && <>
                  <StatBar label="PS"  value={p.stats.ps  || 0} />
                  <StatBar label="Atq" value={p.stats.atq || 0} />
                  <StatBar label="Def" value={p.stats.def || 0} />
                  <StatBar label="SpA" value={p.stats.spa || 0} />
                  <StatBar label="SpD" value={p.stats.spd || 0} />
                  <StatBar label="Vel" value={p.stats.vel || 0} />
                </>}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      <Lightbox src={lightbox} alt={p.nombre} onClose={() => setLightbox(null)} />
    </>
  );
}

export default function Pokedex() {
  const { data: lista, loading } = useApi('/api/pokemon');
  const [selected,   setSelected]   = useState(null);
  const [search,     setSearch]     = useState('');
  const [filterTipo, setFilterTipo] = useState('');

  const filtered = (lista || []).filter(p => {
    const matchSearch = p.nombre.toLowerCase().includes(search.toLowerCase());
    const matchTipo   = !filterTipo || p.tipos.includes(filterTipo);
    return matchSearch && matchTipo;
  });

  const tipos = [...new Set((lista || []).flatMap(p => p.tipos))].sort();

  return (
    <div className="page pokedex-page">
      <div className="container">
        <motion.div className="page-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1>Bloc de Campo <span className="glow-text">— Veltara</span></h1>
          <p className="page-subtitle">
            Registro manuscrito de los especímenes observados en la región.
            <br />
            <span style={{ fontSize: '0.85em', opacity: 0.6 }}>{lista?.length || 0} / 127 especímenes documentados</span>
          </p>
        </motion.div>

        <div className="pokedex-controls">
          <input className="search-input" type="text" placeholder="Buscar espécimen..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="filter-select" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            {tipos.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading && <div className="loading-rune">Consultando el archivo...</div>}

        {!loading && filtered.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">📖</span>
            {search || filterTipo ? 'Ningún espécimen coincide con tu búsqueda.' : 'El bloc de campo está vacío.'}
          </div>
        )}

        <div className="pokedex-grid">
          {filtered.map(p => (
            <motion.div key={p._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <PokemonCard p={p} onClick={setSelected} />
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && <PokemonDetail p={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}